import React, { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPageNew";
import AuthPage from "./pages/AuthPageNew";
import AuthCallback from "./pages/AuthCallback";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import ProfilePage from "./pages/ProfilePage";
import UserProfilePage from "./pages/UserProfilePage";
import SearchPage from "./pages/SearchPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import PricingPage from "./pages/PricingPage";
import PremiumPage from "./pages/PremiumPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import AdminPage from "./pages/AdminPage";
import AdminDashboardNew from "./pages/AdminDashboardNew";
import TermsPage from "./pages/TermsPage";
import CommunityGuidelinesPage from "./pages/CommunityGuidelinesPage";
import PrivacyPage from "./pages/PrivacyPage";
import LegalPage from "./pages/LegalPage";
import SafeCheckPage from "./pages/SafeCheckPage";
import PartnersPage from "./pages/PartnersPage";
import TrackingPage from "./pages/TrackingPage";
import AlertsMapPage from "./pages/AlertsMapPage";
import CirclesPage from "./pages/CirclesPage";
import GPSPage from "./pages/GPSPage";
import UrgentAlertsPage from "./pages/UrgentAlertsPage";
import GoodDeedsPage from "./pages/GoodDeedsPage";
import NotificationsPage from "./pages/NotificationsPage";
import CrushPage from "./pages/CrushPage";
import MissingAlertsPage from "./pages/MissingAlertsPage";
import LocationTracker from "./components/LocationTracker";
import MusicPlayer from "./components/MusicPlayer";
import ChatWidget from "./components/ChatWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem("token");
        setToken(null);
      }
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Login with email/password
  const loginWithCredentials = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  // Login with token and user data (for Google OAuth)
  const login = (newToken, userData) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    // Also logout from Google session if exists
    try {
      await axios.post(`${API}/auth/google/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.warn("Google logout failed:", error.message);
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, loginWithCredentials, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

// App Router - handles OAuth callback detection
const AppRouter = () => {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment synchronously during render for session_id
  // This prevents race conditions by processing OAuth callback FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute><DashboardPage /></ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute><ProfilePage /></ProtectedRoute>
      } />
      <Route path="/user/:userId" element={
        <ProtectedRoute><UserProfilePage /></ProtectedRoute>
      } />
      <Route path="/search" element={
        <ProtectedRoute><SearchPage /></ProtectedRoute>
      } />
      <Route path="/leaderboard" element={
        <ProtectedRoute><LeaderboardPage /></ProtectedRoute>
      } />
      <Route path="/pricing" element={
        <ProtectedRoute><PricingPage /></ProtectedRoute>
      } />
      <Route path="/premium" element={<PremiumPage />} />
      <Route path="/payment/success" element={
        <ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute><AdminPage /></ProtectedRoute>
      } />
      <Route path="/safecheck" element={
        <ProtectedRoute><SafeCheckPage /></ProtectedRoute>
      } />
      <Route path="/safe-check" element={
        <ProtectedRoute><SafeCheckPage /></ProtectedRoute>
      } />
      <Route path="/partners" element={
        <ProtectedRoute><PartnersPage /></ProtectedRoute>
      } />
      <Route path="/alertes" element={
        <ProtectedRoute><AlertsMapPage /></ProtectedRoute>
      } />
      <Route path="/cercles" element={
        <ProtectedRoute><CirclesPage /></ProtectedRoute>
      } />
      <Route path="/gps" element={
        <ProtectedRoute><GPSPage /></ProtectedRoute>
      } />
      <Route path="/urgences" element={
        <ProtectedRoute><UrgentAlertsPage /></ProtectedRoute>
      } />
      <Route path="/bonnes-actions" element={
        <ProtectedRoute><GoodDeedsPage /></ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute><NotificationsPage /></ProtectedRoute>
      } />
      <Route path="/crush" element={
        <ProtectedRoute><CrushPage /></ProtectedRoute>
      } />
      <Route path="/disparitions" element={
        <ProtectedRoute><MissingAlertsPage /></ProtectedRoute>
      } />
      {/* Legal pages - public access */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/community-guidelines" element={<CommunityGuidelinesPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/legal" element={<LegalPage />} />
      {/* Admin Dashboard */}
      <Route path="/admin-dashboard" element={
        <ProtectedRoute><AdminDashboardNew /></ProtectedRoute>
      } />
      {/* Public tracking page */}
      <Route path="/track/:shareToken" element={<TrackingPage />} />
      {/* Password reset pages - public access */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <LocationTracker />
        <MusicPlayer />
        <AppRouter />
        <ChatWidget />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
